// import node module libraries
import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Col, Row, Card, Container, Form, Button, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

// import custom components
import { FormSelect } from 'components/elements/form-select/FormSelect';

// import axios
import axios from 'axios';

// import notification component
import Notification, { handleShowNotification } from 'components/elements/notification/Notification';

const ContactForm = () => {
	const { t } = useTranslation(); // useTranslation-Hook
	const [notification, setNotification] = useState({ variant: '', title: '', message: '' });
	const [isButtonDisabled, setIsButtonDisabled] = useState(false);
	const [isLoading, setIsLoading] = useState(false); // State for loading spinner
	const [selectedSubject, setSelectedSubject] = useState('');
	const [startTime, setStartTime] = useState(Date.now()); // Set start time for bot detection
	const [searchParams] = useSearchParams();
	const subject = searchParams.get("subject");

	useEffect(() => {
		if (subject) {
			setSelectedSubject(t(subject));
		}
	}, [subject]);

	useEffect(() => {
		setStartTime(Date.now());
	}, []);

	const contactSubject = [
		{ value: t('General'), label: t('General') },
		{ value: t('New Flow'), label: t('New Flow')},
		{ value: t('Support'), label: t('Support') },
		{ value: t('Other'), label: t('Other')}
	];

	const handleSubmitMessage = async (e) => {
		e.preventDefault();

		// Check if the form was filled out too quickly
		const endTime = Date.now();
		if (endTime - startTime < 2000) {
			return; // Bot detected
		}
		setIsButtonDisabled(true);
		setIsLoading(true); // Set loading to true

		// API-Call Options
		const options = {
			params: {
			},
			headers: {
				Accept: 'application/json'
			}
		};
		const data = {
			email_template_id: "61313404-e3a0-11ef-8d7a-0242ac120009", // admin_contact_message
			message: {
				from: "haeppy.app <noreply@haeppy.app>",
				to: "hello@haeppy-shopping.com",
				variables: {
					firstname: e.target.firstname.value,
					lastname: e.target.lastname.value,
					email: e.target.email.value,
					phone: e.target.phone ? e.target.phone.value : '',
					subject: e.target.subject.value,
					message: e.target.message.value,
					soruceSystemName: e.target.soruceSystemName ? e.target.soruceSystemName.value : '',
					destSystemName: e.target.destSystemName ? e.target.destSystemName.value : '',
					transmittedData: e.target.transmittedData ? e.target.transmittedData.value : '',
					interfaceSpecification: e.target.interfaceSpecification ? e.target.interfaceSpecification.value : ''
				}
			}
		};

		// Dynamische URL basierend auf der Umgebung
		const baseURL = process.env.REACT_APP_ENV === 'development'
			? `http://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`
			: `https://${process.env.REACT_APP_BACKEND_API_BASE_URI_ENV}`;

		await axios.post(`${baseURL}/email/send-email`, data, options)
			.then(response => {
				handleShowNotification(setNotification, 'info', 'Erfolgreich', 'Deine Nachricht wurde erfolgreich übermittelt. Wir werden uns schnellstmöglich bei dir melden.');
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				e.target.reset();

			})
			.catch((error) => {
				setIsButtonDisabled(false);
				setIsLoading(false); // Stop loading
				const errorMessage = error.response?.data?.message ? error.message + '. ' + error.response.data.message : error.message;
				handleShowNotification(setNotification, 'danger', 'Fehler', errorMessage + '. Deine Nachricht konnte nicht gesendet werden. Bitte versuche es später erneut oder kontaktiere den Support über hello@haeppy-shopping.com.');
			})
	};

	return (
		<div className="px-4 px-xl-20 py-8 py-lg-0">
			{notification.show && (
				<Notification variant={notification.variant} title={notification.title} message={notification.message} show={notification.show} setNotification={setNotification} />
			)}
			{/* form section */}
			<div id="form">
				<Form onSubmit={handleSubmitMessage}>
					<Row>
						{/* First Name */}
						<Col md={6} sm={12}>
							<Form.Group className="mb-3" controlId="firstname">
								<Form.Label>
									{t('First Name')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="text"
									placeholder={t('Enter First Name')}
									autoComplete="first-name"
									required
								/>
							</Form.Group>
						</Col>

						{/* Last Name */}
						<Col md={6} sm={12}>
							<Form.Group className="mb-3" controlId="lastname">
								<Form.Label>
									{t('Last Name')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="text"
									placeholder={t('Enter Last Name')}
									autoComplete="last-name"
									required
								/>
							</Form.Group>
						</Col>

						{/* Company */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="company">
								<Form.Label>
									{t('Company Name')} <span className="text-danger"></span>
								</Form.Label>
								<Form.Control
									type="text"
									placeholder={t('Enter Company Name')}
									autoComplete="billing name"
								/>
							</Form.Group>
						</Col>

						{/* Email */}
						<Col md={6} sm={12}>
							<Form.Group className="mb-3" controlId="email">
								<Form.Label>
									{t('Email')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									type="email"
									placeholder={t('Enter Email')}
									autoComplete="email"
									required />
							</Form.Group>
						</Col>

						{/* Phone Number */}
						<Col md={6} sm={12}>
							<Form.Group className="mb-3" controlId="phone">
								<Form.Label>
									{t('Phone')} <span className="text-danger"></span>
								</Form.Label>
								<Form.Control
									type="number"
									placeholder={t('Enter Phone')}
									autoComplete="tel"
								/>
							</Form.Group>
						</Col>

						{/* Contact Reason */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="subject">
								<Form.Label>
									{t('Subject')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									as={FormSelect}
									placeholder={selectedSubject ? selectedSubject : t('Choose Subject')}
									options={contactSubject}
									value={selectedSubject}
									onChange={(e) => setSelectedSubject(e.target.value)}
									required
								/>
							</Form.Group>
						</Col>

						<div className="py-3">

							{selectedSubject === t('New Flow') && (
								<Col md={12} sm={12}>
									<p className="text-primary">{t('Please provide the following information for new flow:')}</p>
								</Col>
							)}

							{/* Source System Name */}
							{selectedSubject === t('New Flow') && (
								<Col md={12} sm={12}>
									<Form.Group className="mb-3" controlId="sourceSystemName">
										<Form.Label>
											{t('Source System Name')} <span className="text-danger">*</span>
										</Form.Label>
										<Form.Control
											type="text"
											placeholder={t('for e.g. ebay')}
											required
										/>
									</Form.Group>
								</Col>
							)}

							{/* Destination System Name */}
							{selectedSubject === t('New Flow') && (
								<Col md={12} sm={12}>
									<Form.Group className="mb-3" controlId="destSystemName">
										<Form.Label>
											{t('Destination System Name')} <span className="text-danger">*</span>
										</Form.Label>
										<Form.Control
											type="text"
											placeholder={t('for e.g. Shopify')}
											required
										/>
									</Form.Group>
								</Col>
							)}

							{/* Transmitted Data */}
							{selectedSubject === t('New Flow') && (
								<Col md={12} sm={12}>
									<Form.Group className="mb-3" controlId="transmittedData">
										<Form.Label>
											{t('Transmitted Data')} <span className="text-danger">*</span>
										</Form.Label>
										<Form.Control
											type="text"
											placeholder={t('for e.g. Orders or Returns')}
											required
										/>
									</Form.Group>
								</Col>
							)}

							{/* Interface */}
							{selectedSubject === t('New Flow') && (
								<Col md={12} sm={12}>
									<Form.Group className="mb-3" controlId="interfaceSpecification">
										<Form.Label>
											{t('Interface Specification')}
										</Form.Label>
										<Form.Control
											type="text"
											placeholder={t('if known e.g. Link to REST-API Documentation')}
											required
										/>
									</Form.Group>
								</Col>
							)}

						</div>

						{/* Messages */}
						<Col md={12} sm={12}>
							<Form.Group className="mb-3" controlId="message">
								<Form.Label>
									{t('Message')} <span className="text-danger">*</span>
								</Form.Label>
								<Form.Control
									as="textarea"
									placeholder={t('Your Message to us')}
									rows={5}
									required
								/>
							</Form.Group>
						</Col>

						{/* button */}
						<Col md={12} sm={12}>
							<Button variant="primary" type="submit" disabled={isButtonDisabled}>
								{isLoading ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" />
										{t('Send Message')}
									</>
								) : (
									t('Send Message')
								)}
							</Button>
						</Col>
					</Row>
				</Form>
			</div>
		</div>
	);
};
export default ContactForm;